import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { roomService } from "@/services/ServiceRegistry";
import {
  CreateRoomRequest,
  UpdateRoomRequest,
  RoomFilters,
} from "@/types/room";
import type {
  RoomUsageAnalyticsBucket,
  RoomUsageAnalyticsGroupBy,
  RoomUsageAnalyticsResponse,
  RoomUsageHistoryResponse,
} from "@/types/roomUsage";

export const roomKeys = {
  all: ["rooms"] as const,
  lists: () => [...roomKeys.all, "list"] as const,
  list: (
    hotelId: string,
    page?: number | null,
    limit?: number | null,
    filters?: RoomFilters,
  ) =>
    [
      ...roomKeys.lists(),
      hotelId,
      page ?? null,
      limit ?? null,
      filters,
    ] as const,
  details: () => [...roomKeys.all, "detail"] as const,
  detail: (hotelId: string, id: string) =>
    [...roomKeys.details(), hotelId, id] as const,
  usageHistory: (hotelId: string, roomId: string, from: string, to: string) =>
    [...roomKeys.detail(hotelId, roomId), "usage-history", from, to] as const,
  usageAnalytics: (
    hotelId: string,
    from: string,
    to: string,
    groupBy: RoomUsageAnalyticsGroupBy,
    bucket?: RoomUsageAnalyticsBucket,
  ) =>
    [
      ...roomKeys.all,
      "usage-analytics",
      hotelId,
      from,
      to,
      groupBy,
      bucket ?? null,
    ] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;
const RESERVATIONS_QUERY_KEY = ["reservations"] as const;
const SETTLEMENT_QUERY_KEY = ["settlement"] as const;

const invalidateRoomRelatedQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: roomKeys.all });
  queryClient.invalidateQueries({ queryKey: RESERVATIONS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: SETTLEMENT_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

export const useRooms = (
  hotelId: string,
  page: number | null = 1,
  limit: number | null = 10,
  filters?: RoomFilters,
  enabled = true,
) => {
  return useQuery({
    queryKey: roomKeys.list(hotelId, page, limit, filters),
    queryFn: () =>
      roomService.getRooms(
        hotelId,
        filters,
        page || undefined,
        limit || undefined,
      ),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!hotelId,
  });
};

export const useAvailableRooms = (
  hotelId: string,
  startDate: string,
  endDate: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: roomKeys.list(hotelId, null, null, { startDate, endDate }),
    queryFn: () =>
      roomService.getAvailableRooms(hotelId, { startDate, endDate }),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!hotelId && !!startDate && !!endDate,
  });
};

export const useRoom = (id: string | undefined, hotelId: string) => {
  return useQuery({
    queryKey: roomKeys.detail(hotelId, id ?? ""),
    queryFn: () => roomService.getRoomById(id!, hotelId),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useRoomUsageHistory = (
  hotelId: string,
  roomId: string | undefined,
  filters: {
    from: string;
    to: string;
  },
  enabled = true,
) => {
  return useQuery<RoomUsageHistoryResponse>({
    queryKey: roomKeys.usageHistory(
      hotelId,
      roomId ?? "",
      filters.from,
      filters.to,
    ),
    queryFn: () => roomService.getRoomUsageHistory(hotelId, roomId!, filters),
    enabled:
      enabled &&
      !!hotelId &&
      !!roomId &&
      Boolean(filters.from) &&
      Boolean(filters.to),
    staleTime: 5 * 60 * 1000,
  });
};

export const useRoomUsageAnalytics = (
  hotelId: string,
  filters: {
    from: string;
    to: string;
    groupBy: RoomUsageAnalyticsGroupBy;
    bucket?: RoomUsageAnalyticsBucket;
  },
  enabled = true,
) => {
  return useQuery<RoomUsageAnalyticsResponse>({
    queryKey: roomKeys.usageAnalytics(
      hotelId,
      filters.from,
      filters.to,
      filters.groupBy,
      filters.bucket,
    ),
    queryFn: () => roomService.getRoomUsageAnalytics(hotelId, filters),
    enabled:
      enabled && !!hotelId && Boolean(filters.from) && Boolean(filters.to),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roomData: CreateRoomRequest) =>
      roomService.createRoom(roomData),
    onSuccess: () => {
      invalidateRoomRelatedQueries(queryClient);
    },
  });
};

export const useUpdateRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      hotelId,
      data,
    }: {
      id: string;
      hotelId: string;
      data: UpdateRoomRequest;
    }) => roomService.updateRoom(id, hotelId, data),
    onSuccess: (updatedRoom, { hotelId }) => {
      queryClient.setQueryData(
        roomKeys.detail(hotelId, updatedRoom.id),
        (current: any) =>
          current ? { ...current, ...updatedRoom } : updatedRoom,
      );
      queryClient.invalidateQueries({
        queryKey: roomKeys.detail(hotelId, updatedRoom.id),
      });
      invalidateRoomRelatedQueries(queryClient);
    },
  });
};

export const useDeleteRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, hotelId }: { id: string; hotelId: string }) =>
      roomService.deleteRoom(id, hotelId),
    onSuccess: (_, { id, hotelId }) => {
      queryClient.removeQueries({ queryKey: roomKeys.detail(hotelId, id) });
      invalidateRoomRelatedQueries(queryClient);
    },
  });
};

/**
 * Hook to get rooms using centralized context filters
 * This is useful when you want to use the global room filtering state
 */
export const useRoomsWithContextFilters = (contextFilters: RoomFilters) => {
  return useQuery({
    queryKey: ["rooms", "context", contextFilters],
    queryFn: () => roomService.getRoomsWithContextFilters(contextFilters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
